import { supabase } from '@/lib/supabase/client';
import type {
  Model,
  ModelUpdate,
  ModelWithAuthor,
  Profile,
} from '@/lib/supabase/types';
import {
  upsertModelFile,
  createVersionFiles,
  copyFilesToModel,
} from './modelFiles';

// ============================================
// SLUG GENERATION
// ============================================

/**
 * Generate a URL-friendly slug with hash suffix
 * Format: my-model-title-a1b2c3d
 */
export function generateSlug(title: string): string {
  const baseSlug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);

  // Generate short hash (7 chars like git)
  const hash = Math.random().toString(36).substring(2, 9);

  return baseSlug ? `${baseSlug}-${hash}` : hash;
}

/**
 * Check if a slug is available for a user
 */
export async function isSlugAvailable(
  userId: string,
  slug: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from('models')
    .select('id')
    .eq('user_id', userId)
    .eq('slug', slug)
    .is('deleted_at', null)
    .single();

  if (error && error.code === 'PGRST116') {
    // No rows returned means slug is available
    return true;
  }

  return !data;
}

// ============================================
// MODEL CRUD OPERATIONS
// ============================================

// File to be saved with the model
export interface ModelFileInput {
  filename: string;
  content: string | ArrayBuffer;
  fileType:
    | 'scad'
    | 'svg'
    | 'stl'
    | 'dxf'
    | 'dat'
    | 'json'
    | 'txt'
    | 'js'
    | 'png';
}

export interface CreateModelInput {
  title: string;
  description?: string;
  scadCode: string;
  isPublic?: boolean;
  forkedFrom?: string;
  files?: ModelFileInput[];
  downloadPrice?: number | null; // in dollars; stored as cents in DB
}

/**
 * Create a new model with initial version
 */
export async function createModel(
  userId: string,
  input: CreateModelInput,
): Promise<ModelWithAuthor> {
  const slug = generateSlug(input.title);

  // Insert the model
  const { data: model, error: modelError } = await supabase
    .from('models')
    .insert({
      user_id: userId,
      slug,
      title: input.title,
      description: input.description || null,
      scad_code: input.scadCode,
      is_public: input.isPublic ?? true,
      forked_from: input.forkedFrom || null,
      download_price:
        input.downloadPrice != null
          ? Math.round(input.downloadPrice * 100)
          : null,
    })
    .select()
    .single();

  if (modelError) {
    console.error('Error creating model:', modelError);
    throw new Error(modelError.message);
  }

  // Create initial version
  const { data: version, error: versionError } = await supabase
    .from('model_versions')
    .insert({
      model_id: model.id,
      version_number: 1,
      scad_code: input.scadCode,
      commit_message: input.forkedFrom
        ? 'Forked from original'
        : 'Initial version',
    })
    .select()
    .single();

  if (versionError) {
    console.error('Error creating version:', versionError);
    // Don't throw - model was created successfully
  }

  // Update model with current version
  if (version) {
    await supabase
      .from('models')
      .update({ current_version_id: version.id })
      .eq('id', model.id);

    // Save files if provided
    if (input.files && input.files.length > 0) {
      for (const file of input.files) {
        await upsertModelFile(
          model.id,
          file.filename,
          file.content,
          file.fileType,
        );
      }

      // Create version file snapshots
      await createVersionFiles(version.id, model.id);
    }

    // If forking, also copy files from original
    if (input.forkedFrom) {
      await copyFilesToModel(input.forkedFrom, model.id);
      await createVersionFiles(version.id, model.id);
    }
  }

  // Fetch the complete model with author
  return getModelById(model.id) as Promise<ModelWithAuthor>;
}

/**
 * Update an existing model
 */
export async function updateModel(
  modelId: string,
  input: {
    title?: string;
    description?: string;
    scadCode?: string;
    isPublic?: boolean;
    commitMessage?: string;
    files?: ModelFileInput[];
    downloadPrice?: number | null; // in dollars; stored as cents in DB
  },
): Promise<Model> {
  const updates: ModelUpdate = {};

  if (input.title !== undefined) updates.title = input.title;
  if (input.description !== undefined) updates.description = input.description;
  if (input.scadCode !== undefined) updates.scad_code = input.scadCode;
  if (input.isPublic !== undefined) updates.is_public = input.isPublic;
  if ('downloadPrice' in input) {
    updates.download_price =
      input.downloadPrice != null
        ? Math.round(input.downloadPrice * 100)
        : null;
  }

  // Update the model
  const { data: model, error } = await supabase
    .from('models')
    .update(updates)
    .eq('id', modelId)
    .select()
    .single();

  if (error) {
    console.error('Error updating model:', error);
    throw new Error(error.message);
  }

  // If code changed, create a new version
  if (input.scadCode !== undefined) {
    // Get current version count
    const { count } = await supabase
      .from('model_versions')
      .select('*', { count: 'exact', head: true })
      .eq('model_id', modelId);

    const versionNumber = (count || 0) + 1;

    const { data: version } = await supabase
      .from('model_versions')
      .insert({
        model_id: modelId,
        version_number: versionNumber,
        scad_code: input.scadCode,
        commit_message: input.commitMessage || null,
      })
      .select()
      .single();

    if (version) {
      await supabase
        .from('models')
        .update({ current_version_id: version.id })
        .eq('id', modelId);

      // Create version file snapshots
      await createVersionFiles(version.id, modelId);
    }
  }

  // Save files if provided
  if (input.files && input.files.length > 0) {
    for (const file of input.files) {
      await upsertModelFile(
        modelId,
        file.filename,
        file.content,
        file.fileType,
      );
    }
  }

  return model;
}

/**
 * Soft delete a model
 */
export async function deleteModel(modelId: string): Promise<void> {
  const { error } = await supabase
    .from('models')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', modelId);

  if (error) {
    console.error('Error deleting model:', error);
    throw new Error(error.message);
  }
}

/**
 * Fork a model
 */
export async function forkModel(
  userId: string,
  originalModelId: string,
  newTitle?: string,
): Promise<ModelWithAuthor> {
  // Get the original model
  const original = await getModelById(originalModelId);
  if (!original) {
    throw new Error('Original model not found');
  }

  // Create forked model
  return createModel(userId, {
    title: newTitle || `Fork of ${original.title}`,
    description: original.description || undefined,
    scadCode: original.scad_code,
    isPublic: true,
    forkedFrom: originalModelId,
  });
}

// ============================================
// MODEL QUERIES
// ============================================

/**
 * Get a model by ID with author info
 */
export async function getModelById(
  modelId: string,
): Promise<ModelWithAuthor | null> {
  const { data, error } = await supabase
    .from('models')
    .select(
      `
      *,
      author:profiles!user_id(*)
    `,
    )
    .eq('id', modelId)
    .is('deleted_at', null)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching model:', error);
    throw new Error(error.message);
  }

  // Get fork count
  const { data: forkCount } = await supabase.rpc('get_fork_count', {
    model_uuid: modelId,
  });

  return {
    ...data,
    author: data.author as Profile,
    fork_count: forkCount || 0,
  };
}

/**
 * Get a model by username and slug
 */
export async function getModelBySlug(
  username: string,
  slug: string,
): Promise<ModelWithAuthor | null> {
  // First get the user
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', username)
    .single();

  if (profileError || !profile) {
    return null;
  }

  const { data, error } = await supabase
    .from('models')
    .select(
      `
      *,
      author:profiles!user_id(*)
    `,
    )
    .eq('user_id', profile.id)
    .eq('slug', slug)
    .is('deleted_at', null)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching model:', error);
    throw new Error(error.message);
  }

  // Get fork count
  const { data: forkCount } = await supabase.rpc('get_fork_count', {
    model_uuid: data.id,
  });

  return {
    ...data,
    author: data.author as Profile,
    fork_count: forkCount || 0,
  };
}

/**
 * Get public models with pagination
 */
export async function getPublicModels(options?: {
  limit?: number;
  offset?: number;
  sortBy?: 'newest' | 'popular' | 'most-forked';
  search?: string;
}): Promise<{ models: ModelWithAuthor[]; total: number }> {
  const limit = options?.limit || 20;
  const offset = options?.offset || 0;
  const sortBy = options?.sortBy || 'newest';

  let query = supabase
    .from('models')
    .select(
      `
      *,
      author:profiles!user_id(*)
    `,
      { count: 'exact' },
    )
    .eq('is_public', true)
    .is('deleted_at', null);

  // Search filter
  if (options?.search) {
    query = query.or(
      `title.ilike.%${options.search}%,description.ilike.%${options.search}%`,
    );
  }

  // Sorting
  switch (sortBy) {
    case 'popular':
      query = query.order('view_count', { ascending: false });
      break;
    case 'most-forked':
      // Note: This requires fork_count to be computed, using view_count as fallback
      query = query.order('view_count', { ascending: false });
      break;
    case 'newest':
    default:
      query = query.order('created_at', { ascending: false });
  }

  // Pagination
  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching models:', error);
    throw new Error(error.message);
  }

  const models: ModelWithAuthor[] = (data || []).map((model) => ({
    ...model,
    author: model.author as Profile,
    fork_count: 0, // Would need separate query for each
  }));

  return {
    models,
    total: count || 0,
  };
}

/**
 * Get models by user
 */
export async function getModelsByUser(
  userId: string,
  options?: {
    includePrivate?: boolean;
    limit?: number;
    offset?: number;
  },
): Promise<ModelWithAuthor[]> {
  const limit = options?.limit || 50;
  const offset = options?.offset || 0;

  let query = supabase
    .from('models')
    .select(
      `
      *,
      author:profiles!user_id(*)
    `,
    )
    .eq('user_id', userId)
    .is('deleted_at', null)
    .order('updated_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (!options?.includePrivate) {
    query = query.eq('is_public', true);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching user models:', error);
    throw new Error(error.message);
  }

  return (data || []).map((model) => ({
    ...model,
    author: model.author as Profile,
    fork_count: 0,
  }));
}

/**
 * Get models by username
 */
export async function getModelsByUsername(
  username: string,
  options?: {
    includePrivate?: boolean;
    limit?: number;
    offset?: number;
  },
): Promise<ModelWithAuthor[]> {
  // First get the user
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('username', username)
    .single();

  if (profileError || !profile) {
    return [];
  }

  return getModelsByUser(profile.id, options);
}

/**
 * Increment view count for a model
 */
export async function incrementViewCount(modelId: string): Promise<void> {
  await supabase.rpc('increment_model_view_count', { model_uuid: modelId });
}

// ============================================
// THUMBNAIL OPERATIONS
// ============================================

/**
 * Upload a thumbnail for a model
 */
export async function uploadThumbnail(
  modelId: string,
  thumbnailBlob: Blob,
): Promise<string | null> {
  const fileName = `${modelId}.png`;

  const { error: uploadError } = await supabase.storage
    .from('thumbnails')
    .upload(fileName, thumbnailBlob, {
      contentType: 'image/png',
      upsert: true,
    });

  if (uploadError) {
    console.error('Error uploading thumbnail:', uploadError);
    return null;
  }

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from('thumbnails').getPublicUrl(fileName);

  // Update model with thumbnail URL
  await supabase
    .from('models')
    .update({ thumbnail_url: publicUrl })
    .eq('id', modelId);

  return publicUrl;
}
